import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useDetailedInstallationData } from 'src/Libs/installation-details/hooks'
import { DeviceLinkModel, useGetDeviceLinks } from 'src/Libs/installations'
import { useTypedSelector } from 'src/State/RootReducer'
import { FarmContext } from './context'
import { FarmContextValue, FarmMember } from './types'
import { useSmartMeterGatewayDataForStorages } from 'src/Libs/installation-settings/hooks'
import { SmartMeterGatewayModel } from 'src/Libs/installation-settings/model'

type Props = {
  children: React.ReactNode
}

const FarmContextProvider: React.FC<Props> = ({ children }) => {
  const activeStorageId = useTypedSelector(state => state.storage.activeStorage)
  const installationId = useTypedSelector(state => state.storage.installation?.id)?.toString()

  const [installation, isInstallationLoading, , isInstallationError] = useDetailedInstallationData(installationId)
  const { deviceLinks, isLoading: areDeviceLinksLoading } = useGetDeviceLinks(installationId)
  const {
    triggerSmartMeterGatewayDataFetch,
    smartMeterGatewayDataByStorageId,
    isLoading: isSmartMeterGatewayLoading,
  } = useSmartMeterGatewayDataForStorages()

  const farmClientStorageIds = useMemo(() => {
    if (!installation || !deviceLinks) return []

    return deriveFarmClientStorageIds(deviceLinks, installation.emsMasterDeviceId)
  }, [deviceLinks, installation])

  const refetchSmartMeterGatewayData = useCallback(async (): Promise<void> => {
    if (!farmClientStorageIds.length) return

    await triggerSmartMeterGatewayDataFetch(farmClientStorageIds)
  }, [farmClientStorageIds, triggerSmartMeterGatewayDataFetch])

  useEffect(() => {
    void refetchSmartMeterGatewayData()
  }, [refetchSmartMeterGatewayData])

  const farmContextValue: FarmContextValue = useMemo(() => {
    if (!installation || !deviceLinks)
      return {
        isFarm: false,
        isFarmMasterSelected: false,
        isLoading: isInstallationLoading || areDeviceLinksLoading,
        isError: isInstallationError,
        refetchSmartMeterGatewayData,
      }

    const farmMaster = deriveFarmMaster(deviceLinks, installation.emsMasterDeviceId)
    const farmClients = deriveFarmClients(deviceLinks, installation.emsMasterDeviceId, smartMeterGatewayDataByStorageId)

    return {
      isFarm: installation.isFarm || false,
      isFarmMasterSelected: installation.isFarm && installation.emsMasterDeviceId === activeStorageId,
      farmMaster,
      farmClients,
      isLoading: isInstallationLoading || areDeviceLinksLoading || isSmartMeterGatewayLoading,
      isError: isInstallationError,
      refetchSmartMeterGatewayData,
    }
  }, [
    activeStorageId,
    areDeviceLinksLoading,
    deviceLinks,
    installation,
    isInstallationError,
    isInstallationLoading,
    isSmartMeterGatewayLoading,
    smartMeterGatewayDataByStorageId,
    refetchSmartMeterGatewayData,
  ])

  return <FarmContext.Provider value={farmContextValue}>{children}</FarmContext.Provider>
}

const deriveFarmMaster = (deviceLinks: DeviceLinkModel[], farmMasterId: string | undefined): FarmMember | undefined => {
  const masterLink = deviceLinks.find(link => link.deviceId === farmMasterId)

  if (!masterLink?.deviceId) return
  return {
    productType: masterLink.productName,
    storageId: masterLink.deviceId,
  }
}

const deriveFarmClientLinks = (deviceLinks: DeviceLinkModel[], farmMasterId: string | undefined): (DeviceLinkModel & { deviceId: string })[] =>
  deviceLinks.filter(
    (link): link is DeviceLinkModel & { deviceId: string } =>
      link.deviceType === 'storage' &&
      link.deviceId !== farmMasterId &&
      link.deviceId !== null &&
      (link.pairingState === 'paired' || Boolean(link.deviceId))
  )

const deriveFarmClientStorageIds = (deviceLinks: DeviceLinkModel[], farmMasterId: string | undefined): string[] => {
  return deriveFarmClientLinks(deviceLinks, farmMasterId).map(link => link.deviceId)
}

const deriveFarmClients = (
  deviceLinks: DeviceLinkModel[],
  farmMasterId: string | undefined,
  smartMeterGatewayDataByStorageId: Record<string, SmartMeterGatewayModel | undefined>
): FarmMember[] =>
  deriveFarmClientLinks(deviceLinks, farmMasterId).map(link => ({
    productType: link.productName,
    storageId: link.deviceId,
    isSmgwActive: smartMeterGatewayDataByStorageId[link.deviceId]?.isEebusDetectionEnabled || false,
  }))

export default FarmContextProvider

export const useFarmContext = (): FarmContextValue => {
  const farmContext = useContext(FarmContext)

  if (farmContext === undefined) {
    throw new Error('FrameworkView must be used within FarmContext')
  }

  return farmContext
}
